import { useMemo, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { GitFork, Search, ShieldCheck, UserRound, UsersRound } from "lucide-react";
import { PrimaryButton, StatusPill } from "@ocs/ui";
import { adminApi } from "../api";
import { amount } from "../format";

type MarketPosition = {
  position_id: string;
  contract_position_id: string | null;
  identity_type: number;
  product_code: string;
  product_name: string;
  lock_days: number;
  principal_usdt_equivalent: string;
  cumulative_static_earning: string;
  cumulative_dynamic_earning: string;
  earning_cap_amount: string;
  position_status: number;
  started_at: string;
  maturity_at: string;
  exited_at: string | null;
  breach_exit_id: string | null;
  earning_stop_date: string | null;
};

type NodePosition = {
  position_id: string;
  principal_balance: string;
  original_deposit_amount: string;
  position_status: number;
  deposit_confirmed_at: string;
  maturity_at: string;
};

type NetworkAccount = {
  userAddress: string;
  parentAddress: string | null;
  depth: number;
  boundAt: string | null;
  referralCode: string | null;
  accountStatus: number;
  account_earning_stop_date: string | null;
  earningStopped: boolean;
  earningPaused?: boolean;
  earningPermanentlyStopped?: boolean;
  earning_pause_from?: string | null;
  earning_resume_from?: string | null;
  earning_pause_reason?: string | null;
  isNodeIdentity: boolean;
  createdAt: string;
  marketPositionCount: number;
  activeMarketPositionCount: number;
  earningMarketPositionCount: number;
  activeMarketPrincipal: string;
  nodePositionCount: number;
  marketPositions: MarketPosition[];
  nodePositions: NodePosition[];
};

type ReferralNetwork = {
  queryAddress: string;
  root: NetworkAccount | null;
  inviter: NetworkAccount | null;
  descendants: NetworkAccount[];
  summary: {
    totalDescendants: number;
    directDescendants: number;
    maximumDepth: number;
  };
};

const marketStatus = (position: MarketPosition, account: NetworkAccount) => {
  if (position.breach_exit_id) return "收益已停止";
  if (account.earningStopped && position.position_status === 1) {
    return account.earningPaused && !account.earningPermanentlyStopped ? "收益暂停，可恢复" : "账户已停益";
  }
  return ({ 1: "收益中", 2: "退出处理中", 3: "违约退出", 4: "到期退出", 5: "3 倍出局", 6: "3 倍退出中" } as Record<number, string>)[position.position_status] ?? `状态 ${position.position_status}`;
};

const nodeStatus = (status: number) =>
  ({ 1: "有效", 2: "退出处理中", 3: "违约退出", 4: "到期退出", 5: "强制退出" } as Record<number, string>)[status] ?? `状态 ${status}`;

const date = (value?: string | null) => value ? String(value).slice(0, 10) : "—";

function AccountPositions({ account }: { account: NetworkAccount }) {
  if (account.marketPositions.length === 0 && account.nodePositions.length === 0) {
    return <div className="network-position-empty">该账户尚无仓位</div>;
  }
  return (
    <div className="network-position-ledger">
      {account.marketPositions.map((position) => (
        <article key={`market-${position.position_id}`}>
          <div>
            <span>Market · {position.product_name}</span>
            <StatusPill tone={position.breach_exit_id || account.earningStopped && position.position_status === 1 ? "warning" : position.position_status === 1 ? "success" : undefined}>{marketStatus(position, account)}</StatusPill>
          </div>
          <strong>{amount(position.principal_usdt_equivalent)} USDT</strong>
          <dl>
            <div><dt>仓位 ID</dt><dd>{position.contract_position_id ?? position.position_id}</dd></div>
            <div><dt>身份 / 周期</dt><dd>{position.identity_type === 2 ? "节点接入" : "普通参与"} / {position.lock_days} 天</dd></div>
            <div><dt>静态 / 动态累计</dt><dd>{amount(position.cumulative_static_earning)} / {amount(position.cumulative_dynamic_earning)}</dd></div>
            <div><dt>开始 / 到期</dt><dd>{date(position.started_at)} / {date(position.maturity_at)}</dd></div>
            {position.earning_stop_date && <div><dt>收益截止</dt><dd>{date(position.earning_stop_date)}</dd></div>}
          </dl>
        </article>
      ))}
      {account.nodePositions.map((position) => (
        <article key={`node-${position.position_id}`}>
          <div><span>原节点仓位</span><StatusPill tone={position.position_status === 1 ? "success" : undefined}>{nodeStatus(position.position_status)}</StatusPill></div>
          <strong>{amount(position.principal_balance)} USDT</strong>
          <dl>
            <div><dt>仓位 ID</dt><dd>{position.position_id}</dd></div>
            <div><dt>原始入金</dt><dd>{amount(position.original_deposit_amount)} USDT</dd></div>
            <div><dt>确认 / 到期</dt><dd>{date(position.deposit_confirmed_at)} / {date(position.maturity_at)}</dd></div>
          </dl>
        </article>
      ))}
    </div>
  );
}

function NetworkAccountRow({ account, label }: { account: NetworkAccount; label?: string }) {
  const totalPositions = account.marketPositionCount + account.nodePositionCount;
  const stopped = account.earningStopped || account.marketPositions.some((position) => Boolean(position.breach_exit_id));
  return (
    <details className="network-account-row" style={{ "--network-depth": Math.max(0, account.depth) } as CSSProperties}>
      <summary>
        <span className="network-branch" aria-hidden><i /></span>
        <div className="network-address">
          <span>{label ?? (account.depth === 0 ? "查询账户" : `第 ${account.depth} 层`)}</span>
          <code>{account.userAddress}</code>
        </div>
        <div className="network-account-metric"><span>仓位</span><strong>{totalPositions}</strong></div>
        <div className="network-account-metric"><span>收益中</span><strong>{account.earningMarketPositionCount}</strong></div>
        <div className="network-account-metric"><span>有效本金</span><strong>{amount(account.activeMarketPrincipal)}</strong></div>
        <StatusPill tone={stopped ? "warning" : account.earningMarketPositionCount > 0 ? "success" : undefined}>
          {account.earningStopped ? account.earningPaused && !account.earningPermanentlyStopped ? "收益暂停，可恢复" : "账户已停益" : stopped ? "含停收益仓位" : account.earningMarketPositionCount > 0 ? "参与中" : account.isNodeIdentity ? "节点账户" : "暂无有效仓位"}
        </StatusPill>
      </summary>
      <div className="network-account-detail">
        <div className="network-relation-meta">
          <span>上级 <code>{account.parentAddress ?? "—"}</code></span>
          <span>绑定 {date(account.boundAt)}</span>
          <span>邀请码 <code>{account.referralCode ?? "—"}</code></span>
          {account.account_earning_stop_date && <span>停益生效日 {date(account.account_earning_stop_date)}</span>}
          {account.earning_pause_from && <span>暂停生效日 {date(account.earning_pause_from)}</span>}
          {account.earning_pause_from && <span>恢复收益日 {account.earning_resume_from ? date(account.earning_resume_from) : "待通知"}</span>}
          {account.earning_pause_reason && <span>暂停原因 {account.earning_pause_reason}</span>}
        </div>
        <AccountPositions account={account} />
      </div>
    </details>
  );
}

const orderedTree = (root: NetworkAccount, descendants: NetworkAccount[]) => {
  const children = new Map<string, NetworkAccount[]>();
  for (const account of descendants) {
    const key = account.parentAddress ?? "";
    children.set(key, [...(children.get(key) ?? []), account]);
  }
  for (const rows of children.values()) rows.sort((left, right) => left.userAddress.localeCompare(right.userAddress));
  const ordered: NetworkAccount[] = [];
  const visit = (account: NetworkAccount) => {
    ordered.push(account);
    for (const child of children.get(account.userAddress) ?? []) visit(child);
  };
  visit(root);
  return ordered;
};

export function ReferralNetworkPage() {
  const [draft, setDraft] = useState("");
  const [address, setAddress] = useState("");
  const valid = /^0x[a-fA-F0-9]{40}$/.test(address);
  const query = useQuery({
    queryKey: ["referral-network", address],
    queryFn: () => adminApi<ReferralNetwork>(`/referral-network/${address}`),
    enabled: valid
  });
  const data = query.data;
  const tree = useMemo(() => data?.root ? orderedTree(data.root, data.descendants) : [], [data]);
  const accountsWithPositions = tree.filter((account) => account.marketPositionCount + account.nodePositionCount > 0).length;
  return (
    <div className="referral-network-page">
      <form className="node-search" onSubmit={(event) => { event.preventDefault(); setAddress(draft.trim().toLowerCase()); }}>
        <Search size={20} /><label>钱包地址<input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="输入完整的 0x 钱包地址" /></label><PrimaryButton>查询推荐关系</PrimaryButton>
      </form>

      {!address && <div className="instruction-state"><GitFork size={28} /><strong>查询完整推荐网络</strong><span>显示直接推荐人、全部下层账户及其 Market／原节点仓位，不受 100 条列表限制。</span></div>}
      {address && !valid && <p className="form-error">请输入完整的 42 位 EVM 钱包地址。</p>}
      {valid && query.isLoading && <div className="admin-skeleton"><i /><i /><i /></div>}
      {valid && query.isError && <div className="instruction-state"><strong>推荐关系读取失败</strong><span>{query.error instanceof Error ? query.error.message : "管理服务查询失败，请稍后重试。"}</span></div>}
      {data && !data.root && <div className="instruction-state"><strong>没有找到该账户</strong><span>该地址尚未进入 Market 推荐关系账本。</span></div>}

      {data?.root && (
        <>
          <section className="network-summary">
            <div><UserRound /><span>查询账户</span><code>{data.root.userAddress}</code></div>
            <dl>
              <div><dt>直接下层</dt><dd>{data.summary.directDescendants}</dd></div>
              <div><dt>全部下层</dt><dd>{data.summary.totalDescendants}</dd></div>
              <div><dt>最深层级</dt><dd>{data.summary.maximumDepth}</dd></div>
              <div><dt>有仓位账户</dt><dd>{accountsWithPositions}</dd></div>
            </dl>
          </section>

          <section className="network-inviter">
            <header><ShieldCheck /><div><span>直接推荐人</span><small>该账户绑定的上级邀请关系</small></div></header>
            {data.inviter ? <NetworkAccountRow account={data.inviter} label="上级推荐人" /> : <div className="network-position-empty">没有上级推荐人，可能是推荐树根账户。</div>}
          </section>

          <section className="network-tree-panel">
            <header><UsersRound /><div><span>下层完整推荐树</span><small>按关系层级展开；点击任意账户查看 Market 与原节点仓位</small></div><strong>{data.summary.totalDescendants} 个下层账户</strong></header>
            <div className="network-tree" role="tree" aria-label="推荐关系树">
              {tree.map((account) => <NetworkAccountRow key={account.userAddress} account={account} />)}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
